import urls from '@api/urls';
import {useAuth} from '@apps-common/auth-react';
import {NotificationsMenu} from '@apps-common/notifications';
import AppsDropdown from '@apps-common/ui-components/NavBar/AppsDropdown.jsx';
import HelpMenu from '@apps-common/ui-components/NavBar/HelpMenu.jsx';
import NavBar from '@apps-common/ui-components/NavBar/NavBar.jsx';
import {NavButton} from '@apps-common/ui-components/NavBar/NavMenuButton.jsx';
import SettingsSidebar from '@components/sidebars/settings/SettingsSidebar.jsx';
import SettingsIcon from '@mui/icons-material/Settings';
import {Box, Chip, MenuItem, Tooltip} from '@mui/material';
import {pink, purple} from '@mui/material/colors';
import {reset as resetAnalytics, trackEvent} from '@src/analytics';
import {
  DEPLOY_ENV,
  NOTIFICATIONS_ENV,
  SUPERRES_CHMLN_TOUR_ID,
} from '@src/config';
import {useFlags} from '@src/hooks/useFlags.js';
import useMapMode from '@src/hooks/useMapMode.js';
import {useCallback, useState} from 'react';

const EnvironmentChip = () => {
  if (
    window.location.hostname === 'www.planet.com' ||
    window.location.hostname === 'insights.planet.com'
  ) {
    return false;
  }

  const label = import.meta.env.DEV
    ? 'Local'
    : window.location.pathname.startsWith('/acorns/live/main')
      ? 'Internal'
      : 'Branch';

  const tooltipText = `API base: ${urls.base}`;

  return (
    <Tooltip title={tooltipText}>
      <Chip
        label={label}
        shape="square"
        size="small"
        sx={{
          backgroundColor: import.meta.env.DEV ? purple[500] : pink[500],
        }}
      />
    </Tooltip>
  );
};

const TopNavBar = () => {
  const auth = useAuth();
  const {notificationsInPlatformApps, showNotificationSettingsButton} =
    useFlags();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {isBrowserMode} = useMapMode();
  const logout = useCallback(() => {
    trackEvent('Logout Button clicked');

    resetAnalytics();

    auth.signOut();
  }, [auth]);

  const showSuperResTourItem =
    isBrowserMode &&
    document.querySelector('[data-chameleon="super-res-available"]') !== null;

  return (
    <>
      <NavBar
        title={<AppsDropdown appKey="data" environment={DEPLOY_ENV} />}
        user={auth.user?.claims}
        secondary={
          <>
            <Box sx={{marginInlineEnd: 1.2}}>
              <EnvironmentChip />
            </Box>

            {notificationsInPlatformApps && (
              <NotificationsMenu
                env={NOTIFICATIONS_ENV}
                showSettingsButton={showNotificationSettingsButton}
              />
            )}
            <HelpMenu>
              {showSuperResTourItem && (
                <MenuItem
                  onClick={() => {
                    window.chmln?.show(SUPERRES_CHMLN_TOUR_ID);
                  }}
                >
                  Take a SuperRes tour!
                </MenuItem>
              )}
            </HelpMenu>
            <NavButton
              aria-label="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <SettingsIcon fontSize="small" />
            </NavButton>
          </>
        }
        logout={logout}
        sx={{zIndex: 'modal'}}
        environment={DEPLOY_ENV}
        className="pl-mode-dark"
      />

      <SettingsSidebar
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default TopNavBar;
